import { GTG_KENYA_COUNTIES } from "@healthdesk/shared-utils";

/**
 * Healthcare CPC reference benchmarks — planning tiers by US state / Kenya county.
 * Optional ad-format toggle (plain vs YouTube-in-modal) swaps benchmark notes and CPC vs CPM bands;
 * CareMap bills YouTube video slides on CPM (carousel impressions), standard links on CPC.
 *
 * `paletteColor` maps to MUI theme palette keys (see apps/unified-app/src/config/theme.js).
 */

export const AD_RATE_AD_TYPE_TEXT = "text";
export const AD_RATE_AD_TYPE_VIDEO = "video";

export const US_STATES = [
  ["AL", "Alabama"],
  ["AK", "Alaska"],
  ["AZ", "Arizona"],
  ["AR", "Arkansas"],
  ["CA", "California"],
  ["CO", "Colorado"],
  ["CT", "Connecticut"],
  ["DE", "Delaware"],
  ["FL", "Florida"],
  ["GA", "Georgia"],
  ["HI", "Hawaii"],
  ["ID", "Idaho"],
  ["IL", "Illinois"],
  ["IN", "Indiana"],
  ["IA", "Iowa"],
  ["KS", "Kansas"],
  ["KY", "Kentucky"],
  ["LA", "Louisiana"],
  ["ME", "Maine"],
  ["MD", "Maryland"],
  ["MA", "Massachusetts"],
  ["MI", "Michigan"],
  ["MN", "Minnesota"],
  ["MS", "Mississippi"],
  ["MO", "Missouri"],
  ["MT", "Montana"],
  ["NE", "Nebraska"],
  ["NV", "Nevada"],
  ["NH", "New Hampshire"],
  ["NJ", "New Jersey"],
  ["NM", "New Mexico"],
  ["NY", "New York"],
  ["NC", "North Carolina"],
  ["ND", "North Dakota"],
  ["OH", "Ohio"],
  ["OK", "Oklahoma"],
  ["OR", "Oregon"],
  ["PA", "Pennsylvania"],
  ["RI", "Rhode Island"],
  ["SC", "South Carolina"],
  ["SD", "South Dakota"],
  ["TN", "Tennessee"],
  ["TX", "Texas"],
  ["UT", "Utah"],
  ["VT", "Vermont"],
  ["VA", "Virginia"],
  ["WA", "Washington"],
  ["WV", "West Virginia"],
  ["WI", "Wisconsin"],
  ["WY", "Wyoming"],
];

const US_STATE_TIERS = {
  high: ["CA", "NY", "MA", "CT", "NJ", "IL", "WA", "CO", "MD", "VA", "MN", "OR", "NH", "RI", "HI", "AK"],
  low: ["MS", "WV", "AR", "MT", "ND", "SD", "WY", "ID", "NM", "KY", "AL", "OK", "LA", "IA", "KS", "NE"],
};

export const US_TIER_MULT = { high: 1.28, mid: 1.0, low: 0.75 };
export const US_TIER_LABEL = {
  high: "High-competition market",
  mid: "Average market",
  low: "Lower-competition market",
};

export function usStateTier(code) {
  if (!code) return null;
  if (US_STATE_TIERS.high.includes(code)) return "high";
  if (US_STATE_TIERS.low.includes(code)) return "low";
  return "mid";
}

/** @deprecated Prefer `GTG_KENYA_COUNTIES` from `@healthdesk/shared-utils` — kept for existing imports. */
export const KE_COUNTIES = GTG_KENYA_COUNTIES;

const KE_HIGH = ["Nairobi", "Kiambu", "Mombasa", "Nakuru", "Kisumu"];
const KE_MID = ["Uasin Gishu", "Kakamega", "Machakos", "Kajiado", "Nyeri", "Meru", "Kilifi", "Kisii", "Kericho"];

export const KE_TIER_MULT = { high: 1.3, mid: 1.0, low: 0.7 };
export const KE_TIER_LABEL = {
  high: "Major urban centre",
  mid: "Secondary city / region",
  low: "Rural / remote county",
};

export function keCountyTier(countyName) {
  if (!countyName) return null;
  if (KE_HIGH.includes(countyName)) return "high";
  if (KE_MID.includes(countyName)) return "mid";
  return "low";
}

export const US_BASE = [
  {
    id: "hcp",
    label: "Healthcare Providers",
    subtitle: "Physicians, nurses, pharmacists, specialists",
    paletteColor: "primary",
    cpc: { min: 8, typical: [15, 30], premium: 40, premiumSuffix: "+" },
    cpm: { min: 15, typical: [28, 55], premium: 85, premiumSuffix: "+" },
    notes: {
      text: [
        "Specialty targeting drives the biggest cost swings",
        "Verified/NPI-matched data commands steep premiums",
        "Subspecialists (oncology, cardiology) at high end",
        "Authenticated HCP platforms (Epocrates, Medscape) = premium tier",
      ],
      video: [
        "On CareMap, YouTube slides bill CPM when the carousel shows the slide—not CPC on “Watch video”",
        "Healthcare out-stream / in-feed video CPMs often land in mid tens to low hundreds USD industry-wide",
        "Strong creative (MOA, trials, demos) can justify premium CPM in endemic health environments",
        "Short videos improve completion after the user opens the modal; CPM still accrues on impressions",
      ],
    },
  },
  {
    id: "patient",
    label: "Patients / Consumers",
    subtitle: "Condition-targeted or health-interest audiences",
    paletteColor: "success",
    cpc: { min: 1.5, typical: [3, 6], premium: 8, premiumSuffix: "+" },
    cpm: { min: 6, typical: [12, 28], premium: 45, premiumSuffix: "+" },
    notes: {
      text: [
        "Rare disease patients command highest CPCs",
        "High-intent (actively researching treatment) = premium",
        "Broad health interest = lowest cost tier",
        "CTV/video inventory pushes toward premium end",
      ],
      video: [
        "CareMap charges CPM per carousel impression for patient-facing YouTube slides",
        "Consumer health video CPMs are usually lower than HCP, but rare-disease contexts trend higher",
        "Condition education and testimonials work well; impressions still need strong on-carousel copy",
        "Mobile-first: readable headline on the strip matters as much as the video itself",
      ],
    },
  },
  {
    id: "admin",
    label: "Facility Managers / Admins",
    subtitle: "Hospital admins, practice managers, procurement leads",
    paletteColor: "secondary",
    cpc: { min: 5, typical: [10, 18], premium: 25, premiumSuffix: "+" },
    cpm: { min: 10, typical: [20, 42], premium: 65, premiumSuffix: "+" },
    notes: {
      text: [
        "LinkedIn effective but expensive for this segment",
        "ABM targeting specific health systems = premium",
        "Definitive Healthcare data audiences add cost",
        "IDN executives and GPO members at high end",
      ],
      video: [
        "Admin buyers often prefer data sheets; video CPM should be weighed against lean carousel text",
        "≤60 seconds in-modal fits time-poor viewers; CPM still reflects each carousel impression",
        "CareMap CPM is not tied to watch duration or completion",
        "Optional “Learn more” link does not add a second CPC when using video mode",
      ],
    },
  },
];

export const KE_BASE = [
  {
    id: "hcp",
    label: "Healthcare Providers",
    subtitle: "Doctors, nurses, pharmacists, clinical officers",
    paletteColor: "primary",
    cpc: { min: 0.4, typical: [0.9, 3.5], premium: 5.5, premiumSuffix: "+" },
    cpm: { min: 0.8, typical: [1.8, 6], premium: 10, premiumSuffix: "+" },
    notes: {
      text: [
        "Very limited verified HCP targeting available locally",
        "Nairobi-based HCPs dominate the targetable pool",
        "Specialist reach often requires regional/national campaigns",
        "International pharma platforms may not cover Kenya",
      ],
      video: [
        "Mobile-optimized video—most HCPs on smartphone; CareMap CPM accrues on carousel impressions",
        "YouTube penetration is high; Swahili outside Nairobi often outperforms English",
        "Kenya CPM benchmarks are far below US endemic health video, but competition is thinner",
        "Short clinical clips reduce data-cost friction after the user opens the player",
      ],
    },
  },
  {
    id: "patient",
    label: "Patients / Consumers",
    subtitle: "Condition-targeted or health-interest audiences",
    paletteColor: "success",
    cpc: { min: 0.05, typical: [0.12, 0.45], premium: 0.9, premiumSuffix: "+" },
    cpm: { min: 0.12, typical: [0.35, 1.2], premium: 2.5, premiumSuffix: "+" },
    notes: {
      text: [
        "Mobile-first audience — most traffic via smartphone",
        "Lower competition drives significantly reduced CPCs",
        "Condition-specific targeting limited outside major cities",
        "Swahili-language ads often outperform English",
      ],
      video: [
        "Short-form (≤90s) fits mobile data; CPM still reflects each time the slide appears",
        "Health education video is well-received and shareable",
        "CareMap uses CPM for YouTube slides—no CPC on opening the modal",
        "Clear on-strip copy improves perceived quality of each impression",
      ],
    },
  },
  {
    id: "admin",
    label: "Facility Managers / Admins",
    subtitle: "Hospital admins, clinic managers, procurement leads",
    paletteColor: "secondary",
    cpc: { min: 0.25, typical: [0.6, 1.8], premium: 3.0, premiumSuffix: "+" },
    cpm: { min: 0.5, typical: [1.2, 3.5], premium: 6, premiumSuffix: "+" },
    notes: {
      text: [
        "Public health facility managers dominate this segment",
        "Limited programmatic B2B targeting options locally",
        "LinkedIn reach is thin outside Nairobi",
        "County government health offices may require direct outreach",
      ],
      video: [
        "Supply chain and procurement ROI content works best in video",
        "Community impact framing can resonate for county officials",
        "CareMap CPM applies when the carousel shows the video slide",
        "Plain text + landing page remains a strong alternative in this segment",
      ],
    },
  },
];

function scale(v, mult) {
  return Math.round(v * mult * 100) / 100;
}

/**
 * Apply geographic tier multiplier to baseline audience rows (CPC planning).
 * @param {typeof US_BASE[0]} base
 * @param {number} geoMult
 */
export function applyAdRateMults(base, geoMult) {
  const out = {
    ...base,
    cpc: {
      ...base.cpc,
      min: scale(base.cpc.min, geoMult),
      typical: [scale(base.cpc.typical[0], geoMult), scale(base.cpc.typical[1], geoMult)],
      premium: scale(base.cpc.premium, geoMult),
      premiumSuffix: base.cpc.premiumSuffix,
    },
  };
  if (base.cpm) {
    out.cpm = {
      ...base.cpm,
      min: scale(base.cpm.min, geoMult),
      typical: [scale(base.cpm.typical[0], geoMult), scale(base.cpm.typical[1], geoMult)],
      premium: scale(base.cpm.premium, geoMult),
      premiumSuffix: base.cpm.premiumSuffix,
    };
  }
  return out;
}

export function formatAdRateMoney(n) {
  if (n < 1) return `$${n.toFixed(2)}`;
  if (n % 1 === 0) return `$${n}`;
  return `$${n.toFixed(2)}`;
}

export const REFERENCE_CAVEATS = [
  "On CareMap, standard carousel links bill CPC on eligible click; YouTube “Link to video” slides bill CPM on each carousel impression (not on opening the modal, and not on watch completion)",
  "US state rates reflect market competition tiers — high, average, or lower-competition",
  "Kenya rates estimated from regional programmatic data; HCP targeting limited outside Nairobi",
  "Verified/authenticated audience data always commands a premium over inferred targeting",
  "Including a logo improves CTR 15–30% with minimal cost impact",
  "HIPAA-compliant segments (US) cost more than general health interest audiences",
  "Open programmatic exchange = lowest cost; PMP deals and direct buys push higher",
  "ZIP codes are USPS delivery areas: most map cleanly to one state, but a small number span borders or serve unusual areas—use state/county here as planning tiers, not a substitute for your exact geo targets",
];
